import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useProjectContext } from "../../../../hooks/useProjectContext";
import { useInspections } from "../../../../hooks/useInspections";
import { usePhotos } from "../../../../hooks/usePhotos";
import InspectionForm from "../../../modals/forms/InspectionForm";
import InspectionsHeader from "./components/InspectionsHeader";
import InspectionsActionsBar from "./components/InspectionsActionsBar";
import InspectionsListTable from "./components/InspectionsListTable";
import InspectionDrawer from "./components/InspectionDrawer";
import InspectionAssignmentModal from "./components/InspectionAssignmentModal";
import SummaryTab from "./components/tabs/SummaryTab";
import ChecklistTab from "./components/tabs/ChecklistTab";
import FindingsTab from "./components/tabs/FindingsTab";
import { useProjectTeamOptions } from "./hooks/useProjectTeamOptions";
import { useInspectionBundle } from "./hooks/useInspectionBundle";
import { useInspectionActions } from "./hooks/useInspectionActions";
import { useRowActions } from "./hooks/useRowActions";
import { isSystemAdmin } from "../../../../constants/systemRoles";
import InspectorPerformanceView from "./InspectorPerformanceView";
import { ClipboardCheck, BarChart3 } from "lucide-react";
import { projectsAPI, inspectionsAPI, llmAPI } from "../../../../services/api";
import { documentsAPI } from "../../../../services/documentsAPI";

const EMPTY_FORM = {
  inspection_date: "",
  inspection_type: "",
  inspection_type_ids: [],
  location: "",
  site_location: "",

  precipitation_type: "",
  precipitation_amount: "",
  wind: "",
  temperature_high: "",
  temperature_low: "",

  weather_conditions: "",
  notes: "",
};

const EMPTY_PHOTO_META = { caption: "", tags: "", description: "" };

const apiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    try {
      return JSON.stringify(detail);
    } catch {
      // Ignore serialization failure and use the normal fallback below.
    }
  }
  return error?.message || fallback;
};

const isPdfDocument = (doc) => {
  const type = (doc?.document_type || "").toLowerCase();
  const name = (doc?.document_name || "").toLowerCase();
  const path = (doc?.file_path || "").toLowerCase();
  return type === "pdf" || name.endsWith(".pdf") || path.endsWith(".pdf");
};

const InspectionsView = () => {
  const { activeProject, projectRole, currentUser } = useProjectContext();
  const projectId = activeProject?.id;

  const { inspections, loading, creating, error, create, stats, reload } =
    useInspections();

  const {
    photos,
    loading: photosLoading,
    uploading: photoUploading,
    error: photosError,
    upload: uploadPhoto,
    remove: removePhoto,
  } = usePhotos();

  const { teamOptions, teamLoading } = useProjectTeamOptions(projectId);

  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [inspectionTypesLoading, setInspectionTypesLoading] = useState(false);

  const loadInspectionTypes = useCallback(async () => {
    if (!projectId) return;

    setInspectionTypesLoading(true);

    try {
      const res = await inspectionsAPI.listProjectInspectionTypes(projectId);
      setInspectionTypes(res.data || []);
    } catch (err) {
      console.error("Failed to load project inspection types", err);
      setInspectionTypes([]);
    } finally {
      setInspectionTypesLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadInspectionTypes();
  }, [loadInspectionTypes]);

  const bundle = useInspectionBundle(projectId);

  const {
    open: openBundle,
    close: closeBundle,
    setChecklist,
    setFindings,
    setDetail: setBundleDetail,
    openId,
  } = bundle;

  const { checklist, detail: bundleDetail } = bundle;

  const actions = useInspectionActions({
    projectId,
    openId: bundle.openId,
    reload,
    openBundle: bundle.open,
  });

  const rowActions = useRowActions({
    projectId,
    reload,
    openId: bundle.openId,
    openBundle: bundle.open,
  });

  const [showForm, setShowForm] = useState(false);
  const [showList, setShowList] = useState(true);
  const [activeInspectionsTab, setActiveInspectionsTab] = useState("list");
  const [form, setForm] = useState(EMPTY_FORM);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const openCreateInspection = async () => {
    if (!projectId) return;

    setCreateError("");

    const res = await projectsAPI.getOne(projectId);
    const project = res.data;

    const today = new Date().toISOString().slice(0, 10);

    setForm({
      ...EMPTY_FORM,
      inspection_date: today,
      location:
        project.latitude != null && project.longitude != null
          ? `${project.latitude},${project.longitude}`
          : "",
      site_location: "",
    });

    setShowForm(true);
  };

  const filteredInspections = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();

    const inspectionSortNumber = (inspection) => {
      const value = Number(inspection?.inspection_number);
      return Number.isFinite(value) ? value : Number(inspection?.id || 0);
    };

    const ordered = [...(inspections || [])].sort(
      (a, b) => inspectionSortNumber(b) - inspectionSortNumber(a),
    );

    if (!q) return ordered;

    return ordered.filter((i) => {
      const haystack = [
        i.inspection_number,
        i.inspection_date,
        i.inspector?.last_name,
        i.inspector?.first_name,
        i.created_by_name,
        i.inspection_type,
        i.site_location,
        i.location,
        i.status,
        i.findings_count,
        i.critical_issues_count,
      ]
        .filter((v) => v !== null && v !== undefined)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [inspections, searchTerm]);

  const onCreateFieldChange = useCallback((field, val) => {
    setForm((p) => ({ ...p, [field]: val }));
  }, []);

  const onSubmitCreate = useCallback(
    async (e) => {
      e.preventDefault();
      setCreateLoading(true);
      setCreateError("");

      try {
        await create({
          ...form,
          inspection_date: form.inspection_date || null,
        });

        setShowForm(false);
        setForm(EMPTY_FORM);
        await reload?.();
      } catch (err) {
        setCreateError(
          apiErrorMessage(err, "Failed to create inspection."),
        );
      } finally {
        setCreateLoading(false);
      }
    },
    [create, form, reload],
  );

  const [editSummary, setEditSummary] = useState({});
  const [checklistWarnings, setChecklistWarnings] = useState([]);

  const detail = bundle.detail;

  useEffect(() => {
    if (!detail) return;

    let ids =
      Array.isArray(detail.inspection_type_ids) &&
      detail.inspection_type_ids.length > 0
        ? detail.inspection_type_ids
        : [];

    if (!ids.length && detail.inspection_type && inspectionTypes?.length) {
      const normalizedNames = detail.inspection_type
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      ids = inspectionTypes
        .filter((t) => normalizedNames.includes((t.name || "").trim()))
        .map((t) => t.id);
    }

    setEditSummary({
      inspection_date: detail.inspection_date || "",
      inspection_type: detail.inspection_type || "",
      inspection_type_ids: ids,
      location: detail.location || "",
      site_location: detail.site_location || "",

      precipitation_type: detail.precipitation_type || "",
      precipitation_amount: detail.precipitation_amount || "",
      wind: detail.wind || "",
      temperature_high: detail.temperature_high || "",
      temperature_low: detail.temperature_low || "",

      weather_conditions: detail.weather_conditions || "",
      notes: detail.notes || "",
    });
  }, [detail, inspectionTypes]);

  const [photoFile, setPhotoFile] = useState(null);
  const [photoMeta, setPhotoMeta] = useState(EMPTY_PHOTO_META);

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);

  const [inspectionPdfs, setInspectionPdfs] = useState([]);
  const [availableInspectionPdfs, setAvailableInspectionPdfs] = useState([]);
  const [inspectionPdfsLoading, setInspectionPdfsLoading] = useState(false);
  const [linkingInspectionPdfId, setLinkingInspectionPdfId] = useState(null);

  const onPhotoMetaChange = useCallback((field, value) => {
    setPhotoMeta((p) => ({ ...p, [field]: value }));
  }, []);

  const loadInspectionPdfs = useCallback(async () => {
    if (!projectId || !bundleDetail?.id) return;

    setInspectionPdfsLoading(true);

    try {
      const res = await documentsAPI.listByProject(projectId);
      const docs = Array.isArray(res.data) ? res.data : [];

      const pdfDocs = docs.filter(isPdfDocument);

      const currentInspectionId = Number(bundleDetail.id);
      const linkedIds = (doc) =>
        (Array.isArray(doc?.inspection_ids) ? doc.inspection_ids : []).map(Number);

      setInspectionPdfs(
        pdfDocs.filter((d) => linkedIds(d).includes(currentInspectionId)),
      );

      // Many-to-many: a project PDF can be linked to several inspections.
      // Offer every PDF that is not already linked to the current inspection.
      setAvailableInspectionPdfs(
        pdfDocs.filter((d) => !linkedIds(d).includes(currentInspectionId)),
      );
    } finally {
      setInspectionPdfsLoading(false);
    }
  }, [projectId, bundleDetail?.id]);

  useEffect(() => {
    loadInspectionPdfs();
  }, [loadInspectionPdfs]);

  const doSubmitInspectionPdf = useCallback(
    async (e) => {
      e.preventDefault();

      if (!projectId || !bundleDetail?.id || !pdfFile) return;

      setPdfUploading(true);

      try {
        const fd = new FormData();

        fd.append("file", pdfFile);
        fd.append("document_name", pdfFile.name);
        fd.append("inspection_id", String(bundleDetail.id));
        fd.append("document_type", "pdf");
        fd.append("category", "inspection_pdf");
        fd.append("status", "draft");
        fd.append("rag_mode", "manual");

        await documentsAPI.upload(projectId, fd);
        await loadInspectionPdfs();

        setPdfFile(null);
      } finally {
        setPdfUploading(false);
      }
    },
    [projectId, bundleDetail?.id, pdfFile, loadInspectionPdfs],
  );

  const doLinkExistingInspectionPdf = useCallback(
    async (documentId) => {
      if (!projectId || !bundleDetail?.id || !documentId) return;

      setLinkingInspectionPdfId(documentId);
      try {
        await documentsAPI.linkToInspection(
          projectId,
          bundleDetail.id,
          documentId,
        );
        await loadInspectionPdfs();
      } finally {
        setLinkingInspectionPdfId(null);
      }
    },
    [projectId, bundleDetail?.id, loadInspectionPdfs],
  );

  const doUnlinkInspectionPdf = useCallback(
    async (documentId) => {
      if (!projectId || !bundleDetail?.id || !documentId) return;

      const ok = window.confirm(
        "Remove this PDF from the inspection? The file will remain in Document Control.",
      );
      if (!ok) return;

      try {
        await documentsAPI.unlinkFromInspection(
          projectId,
          bundleDetail.id,
          documentId,
        );
        await loadInspectionPdfs();
      } catch (err) {
        window.alert(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to remove this document from the inspection.",
        );
      }
    },
    [projectId, bundleDetail?.id, loadInspectionPdfs],
  );

  const photosForInspection = useMemo(() => {
    if (!bundle.detail?.id) return [];
    return (photos || []).filter((p) => p.inspection_id === bundle.detail.id);
  }, [photos, bundle.detail?.id]);

  const isAdmin = isSystemAdmin(currentUser?.role);
  const [assignmentInspection, setAssignmentInspection] = useState(null);
  const canAssignInspection =
    isAdmin ||
    projectRole === "qa_qc_manager" ||
    projectRole === "project_manager" ||
    projectRole === "inspector";

  const canManageTemplates =
    isAdmin ||
    projectRole === "qa_qc_manager" ||
    projectRole === "project_manager";

  const canDeleteInspection = isAdmin || projectRole === "qa_qc_manager";
  const canApproveCloseFinding = isAdmin || projectRole === "qa_qc_manager";

  const doOpen = useCallback(
    async (id, initialTab = "summary") => {
      await openBundle(id, initialTab);
    },
    [openBundle],
  );

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (
        !["openInspection", "openFinding"].includes(detail.action) &&
        !detail.inspectionId
      )
        return;
      if (
        detail.projectId &&
        projectId &&
        Number(detail.projectId) !== Number(projectId)
      )
        return;
      if (!detail.inspectionId) return;
      doOpen(detail.inspectionId, detail.findingId ? "findings" : "summary");
    };
    window.addEventListener("smokyq:notification-open", handler);
    return () =>
      window.removeEventListener("smokyq:notification-open", handler);
  }, [projectId, doOpen]);

  const doCloseDrawer = useCallback(() => {
    closeBundle();
  }, [closeBundle]);

  const setL = actions.setL;

  const doSaveSummary = useCallback(async () => {
    if (!projectId || !bundleDetail?.id) return null;

    setL("saveSummary", true);

    try {
      const res = await inspectionsAPI.update(projectId, bundleDetail.id, {
        // Simplified Summary tab only saves visible summary fields.
        inspection_type: editSummary.inspection_type || null,
        inspection_type_ids:
          Array.isArray(editSummary.inspection_type_ids) &&
          editSummary.inspection_type_ids.length > 0
            ? editSummary.inspection_type_ids
            : null,
        site_location: editSummary.site_location || null,
        notes: editSummary.notes || null,
      });

      // Apply the saved Summary immediately. Do not reload checklist/findings just
      // because QA/QC corrected wording in the Summary tab.
      if (res?.data) {
        setBundleDetail?.(res.data);
      }
      await reload?.();
      return res?.data || null;
    } finally {
      setL("saveSummary", false);
    }
  }, [projectId, bundleDetail?.id, editSummary, reload, setBundleDetail, setL]);

  const doConsistencyCheck = useCallback(async () => {
    const rawId = bundle?.detail?.id ?? bundle?.openId;
    const inspectionId = Number(rawId);

    if (!projectId || !Number.isFinite(inspectionId)) return;

    try {
      const res = await inspectionsAPI.consistencyCheck(
        projectId,
        inspectionId,
        {},
      );

      setChecklistWarnings(res.data?.warnings || []);
    } catch {
      setChecklistWarnings([]);
    }
  }, [projectId, bundle?.detail?.id, bundle?.openId]);

  const doCompleteChecklist = useCallback(
    async ({ notes } = {}) => {
      const rawId = bundle?.detail?.id ?? bundle?.openId;
      const inspectionId = Number(rawId);

      if (!projectId || !Number.isFinite(inspectionId)) return;

      actions.setL("completeChecklist", true);

      try {
        await inspectionsAPI.completeChecklist(projectId, inspectionId, {
          notes: (notes ?? checklist?.notes ?? "") || "",
        });

        const cRes = await inspectionsAPI.getChecklist(projectId, inspectionId);
        setChecklist(cRes.data);
        await doConsistencyCheck();
      } finally {
        actions.setL("completeChecklist", false);
      }
    },
    [
      projectId,
      bundle?.detail?.id,
      bundle?.openId,
      checklist?.notes,
      setChecklist,
      actions,
      doConsistencyCheck,
    ],
  );

  const doNaSuggestions = useCallback(async () => {
    const rawId = bundle?.detail?.id ?? bundle?.openId;
    const inspectionId = Number(rawId);

    if (!projectId || !Number.isFinite(inspectionId)) return null;

    const ctx = {
      context: {
        summary: bundleDetail?.notes || "",
        inspection_type: bundleDetail?.inspection_type || "",
        location: bundleDetail?.site_location || bundleDetail?.location || "",
        weather: bundleDetail?.weather_conditions || "",
      },
    };

    const res = await inspectionsAPI.naSuggestions(
      projectId,
      inspectionId,
      ctx,
    );

    return res.data?.suggestions || [];
  }, [projectId, bundle?.detail?.id, bundle?.openId, bundleDetail]);

  const doRewriteText = useCallback(async (text) => {
    const res = await llmAPI.rewrite(text, "professional", true);
    return res.data?.rewritten || "";
  }, []);

  const doAddFinding = useCallback(async () => {
    if (!projectId || !bundleDetail?.id) return;

    actions.setL("addFinding", true);

    try {
      await inspectionsAPI.createFinding(projectId, bundleDetail.id, {
        description: "",
        severity: "minor",
        location: bundleDetail.site_location || bundleDetail.location || null,
        status: "open",
        corrective_action: "",
        responsible_user_id: null,
        due_date: null,
      });

      const fRes = await inspectionsAPI.listFindings(
        projectId,
        bundleDetail.id,
      );

      setFindings(fRes.data || []);
      await reload?.();
    } finally {
      actions.setL("addFinding", false);
    }
  }, [
    projectId,
    bundleDetail?.id,
    bundleDetail?.location,
    bundleDetail?.site_location,
    setFindings,
    reload,
    actions,
  ]);

  const doSaveChecklistDraft = useCallback(
    async (updates) => {
      const rawId = bundle?.detail?.id ?? bundle?.openId;
      const inspectionId = Number(rawId);

      if (!projectId || !Number.isFinite(inspectionId)) return;

      actions.setL("saveChecklistDraft", true);

      try {
        const res = await inspectionsAPI.bulkUpdateChecklistItems(
          projectId,
          inspectionId,
          updates,
        );

        setChecklist(res.data);
        await doConsistencyCheck();
      } finally {
        actions.setL("saveChecklistDraft", false);
      }
    },
    [
      projectId,
      bundle?.detail?.id,
      bundle?.openId,
      actions,
      setChecklist,
      doConsistencyCheck,
    ],
  );

  const [findingSaving, setFindingSaving] = useState({});
  const [findingClosing, setFindingClosing] = useState({});
  const [findingDeleting, setFindingDeleting] = useState({});

  const doSaveFinding = useCallback(
    async (finding, draft, onDone) => {
      if (!projectId || !bundleDetail?.id) return;

      setFindingSaving((p) => ({ ...p, [finding.id]: true }));

      try {
        await inspectionsAPI.updateFinding(
          projectId,
          bundleDetail.id,
          finding.id,
          {
            description:
              (draft.description ?? finding.description ?? "").trim() ||
              finding.description,
            severity: draft.severity || null,
            location: draft.location || null,
            status: draft.status || null,
            corrective_action: draft.corrective_action || null,
            responsible_user_id: draft.responsible_user_id ?? null,
            due_date: draft.due_date || null,
          },
        );

        const fRes = await inspectionsAPI.listFindings(
          projectId,
          bundleDetail.id,
        );

        setFindings(fRes.data || []);
        await reload?.();
        onDone?.();
      } finally {
        setFindingSaving((p) => ({ ...p, [finding.id]: false }));
      }
    },
    [projectId, bundleDetail?.id, setFindings, reload],
  );

  const doCloseFinding = useCallback(
    async (findingId) => {
      if (!projectId || !bundleDetail?.id) return;

      const confirmed = window.confirm(
        "Approve / Close this issue? This records an authorized resolving review and closes the issue.",
      );
      if (!confirmed) return;

      setFindingClosing((p) => ({ ...p, [findingId]: true }));

      try {
        await inspectionsAPI.closeFinding(
          projectId,
          bundleDetail.id,
          findingId,
          {},
        );

        const fRes = await inspectionsAPI.listFindings(
          projectId,
          bundleDetail.id,
        );

        setFindings(fRes.data || []);
        await reload?.();
      } catch (err) {
        window.alert(
          err?.response?.data?.detail ||
            err?.message ||
            "Failed to approve / close issue.",
        );
      } finally {
        setFindingClosing((p) => ({ ...p, [findingId]: false }));
      }
    },
    [projectId, bundleDetail?.id, setFindings, reload],
  );

  const doDeleteFinding = useCallback(
    async (findingId) => {
      if (!projectId || !bundleDetail?.id) return;

      if (!window.confirm("Delete this finding? This action cannot be undone."))
        return;

      setFindingDeleting((p) => ({ ...p, [findingId]: true }));

      try {
        await inspectionsAPI.deleteFinding(
          projectId,
          bundleDetail.id,
          findingId,
        );

        const fRes = await inspectionsAPI.listFindings(
          projectId,
          bundleDetail.id,
        );

        setFindings(fRes.data || []);
        await reload?.();
      } finally {
        setFindingDeleting((p) => ({ ...p, [findingId]: false }));
      }
    },
    [projectId, bundleDetail?.id, setFindings, reload],
  );

  const doSubmitPhoto = useCallback(
    async (e) => {
      e.preventDefault();

      if (!bundleDetail?.id || !photoFile) return;

      actions.setL("uploadPhoto", true);

      try {
        const fd = new FormData();

        fd.append("file", photoFile);
        fd.append("caption", photoMeta.caption || "");
        fd.append("description", photoMeta.description || "");
        fd.append("tags", photoMeta.tags || "");
        fd.append("inspection_id", String(bundleDetail.id));

        await uploadPhoto(fd);

        setPhotoFile(null);
        setPhotoMeta(EMPTY_PHOTO_META);
      } finally {
        actions.setL("uploadPhoto", false);
      }
    },
    [bundleDetail?.id, photoFile, photoMeta, uploadPhoto, actions],
  );

  const doDeleteInspection = useCallback(
    async (inspectionId) => {
      if (!projectId) return;

      if (
        !window.confirm("Delete this inspection? This action cannot be undone.")
      )
        return;

      await inspectionsAPI.delete(projectId, inspectionId);
      await reload?.();

      if (openId === inspectionId) closeBundle();
    },
    [projectId, reload, openId, closeBundle],
  );

  const doRowRejectPrompt = useCallback(
    (inspectionId) =>
      rowActions.run(inspectionId, "reject", async () =>
        actions.rejectPrompt(inspectionId),
      ),
    [rowActions, actions],
  );

  const busyOverlay =
    bundle.detailLoading ||
    bundle.checklistLoading ||
    bundle.findingsLoading ||
    actions.busy;

  if (!activeProject) return null;

  const canViewInspectorPerformance = isAdmin || projectRole === "qa_qc_manager";
  return (
    <div className="space-y-6 relative">
      {canViewInspectorPerformance && (
        <nav aria-label="Inspections sections" role="tablist"
          className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
          {[
            { id: "list", text: "Inspection List", Icon: ClipboardCheck },
            { id: "performance", text: "Inspector Performance", Icon: BarChart3 },
          ].map(({ id, text, Icon }) => (
            <button key={id} type="button" role="tab"
              aria-selected={activeInspectionsTab === id}
              onClick={() => setActiveInspectionsTab(id)}
              className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-600 ${
                activeInspectionsTab === id
                  ? "border-orange-600 bg-orange-600 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:border-orange-300 hover:text-orange-700"
              }`}>
              <Icon className="h-4 w-4" aria-hidden="true" />
              {text}
            </button>
          ))}
        </nav>
      )}
      {activeInspectionsTab === "performance" && canViewInspectorPerformance &&
        <InspectorPerformanceView projectId={projectId} onOpenInspection={(id) => {
          setActiveInspectionsTab("list");
          doOpen(id);
        }} />}
      {activeInspectionsTab === "list" && <>
      <InspectionsHeader stats={stats} />

      <InspectionsActionsBar
        projectName={activeProject.name}
        projectRole={projectRole}
        showList={showList}
        onToggleNew={() => {
          openCreateInspection();
        }}
        onToggleList={() => {
          setShowList((v) => !v);
          setShowForm(false);
        }}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {showForm && (
        <div className="space-y-3">
          {createError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {createError}
            </div>
          ) : null}
          <InspectionForm
            value={form}
            onChange={onCreateFieldChange}
            onSubmit={onSubmitCreate}
            onCancel={() => {
              setShowForm(false);
              setCreateError("");
            }}
            creating={creating || createLoading}
            projectId={projectId}
            projectRole={projectRole}
            inspectionTypes={inspectionTypes}
            inspectionTypesLoading={inspectionTypesLoading}
          />
        </div>
      )}

      {showList && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b text-sm font-medium text-gray-800">
            Inspection List
          </div>

          <InspectionsListTable
            inspections={filteredInspections}
            loading={loading}
            error={error}
            projectRole={projectRole}
            canDeleteInspection={canDeleteInspection}
            rowLoading={rowActions.rowLoading}
            onOpen={doOpen}
            onDelete={doDeleteInspection}
            onSubmit={(id) =>
              rowActions.run(id, "submit", () =>
                inspectionsAPI.submit(projectId, id),
              )
            }
            onApprove={(id) =>
              rowActions.run(id, "approve", () =>
                inspectionsAPI.approve(projectId, id),
              )
            }
            onRejectPrompt={doRowRejectPrompt}
            onReopen={(id) =>
              rowActions.run(id, "reopen", () =>
                inspectionsAPI.reopen(projectId, id),
              )
            }
            onAssignInspection={
              canAssignInspection ? setAssignmentInspection : null
            }
          />
        </div>
      )}

      <InspectionDrawer
        open={!!bundle.openId}
        onClose={doCloseDrawer}
        tab={bundle.tab}
        setTab={bundle.setTab}
        detail={bundle.detail}
        openId={bundle.openId}
        projectRole={projectRole}
        projectId={projectId}
        busyOverlay={busyOverlay}
        actionLoading={actions.loading}
        onSubmit={actions.submit}
        onApprove={actions.approve}
        onReject={() => actions.rejectPrompt(bundle.openId)}
        onReopen={actions.reopen}
        onAssignInspection={
          canAssignInspection ? setAssignmentInspection : null
        }
        SummaryTab={
          <SummaryTab
            detail={bundle.detail}
            detailLoading={bundle.detailLoading}
            editSummary={editSummary}
            setEditSummary={setEditSummary}
            onSaveSummary={doSaveSummary}
            savingSummary={actions.loading.saveSummary}
            photosForInspection={photosForInspection}
            photosLoading={photosLoading}
            photosError={photosError}
            removePhoto={removePhoto}
            photoMeta={photoMeta}
            onPhotoMetaChange={onPhotoMetaChange}
            photoFile={photoFile}
            setPhotoFile={setPhotoFile}
            onSubmitPhoto={doSubmitPhoto}
            uploadingPhoto={photoUploading || actions.loading.uploadPhoto}
            pdfFile={pdfFile}
            setPdfFile={setPdfFile}
            onSubmitInspectionPdf={doSubmitInspectionPdf}
            pdfUploading={pdfUploading}
            inspectionPdfs={inspectionPdfs}
            availableInspectionPdfs={availableInspectionPdfs}
            inspectionPdfsLoading={inspectionPdfsLoading}
            linkingInspectionPdfId={linkingInspectionPdfId}
            onLinkExistingInspectionPdf={doLinkExistingInspectionPdf}
            onUnlinkInspectionPdf={doUnlinkInspectionPdf}
            projectId={projectId}
            projectRole={projectRole}
            inspectionTypes={inspectionTypes}
            inspectionTypesLoading={inspectionTypesLoading}
          />
        }
        ChecklistTab={
          <ChecklistTab
            detail={bundle.detail}
            checklist={bundle.checklist}
            checklistLoading={bundle.checklistLoading}
            checklistForbidden={bundle.checklistForbidden}
            onSaveDraft={doSaveChecklistDraft}
            savingDraft={actions.loading.saveChecklistDraft}
            onCompleteChecklist={doCompleteChecklist}
            completingChecklist={actions.loading.completeChecklist}
            warnings={checklistWarnings}
            onRunConsistencyCheck={doConsistencyCheck}
            onSuggestNa={doNaSuggestions}
            onRewriteText={doRewriteText}
          />
        }
        FindingsTab={
          <FindingsTab
            projectId={projectId}
            inspectionId={bundleDetail?.id}
            inspectionSiteLocation={
              bundleDetail?.site_location || bundleDetail?.location || ""
            }
            forbidden={bundle.findingsForbidden}
            loading={bundle.findingsLoading}
            findings={bundle.findings}
            teamOptions={teamOptions}
            teamLoading={teamLoading}
            onAddFinding={doAddFinding}
            adding={actions.loading.addFinding}
            onSaveFinding={doSaveFinding}
            onCloseFinding={doCloseFinding}
            canCloseFinding={canApproveCloseFinding}
            onDeleteFinding={doDeleteFinding}
            savingMap={findingSaving}
            closingMap={findingClosing}
            deletingMap={findingDeleting}
          />
        }
        canManageTemplates={canManageTemplates}
        inspectionTypes={inspectionTypes}
        inspectionTypesLoading={inspectionTypesLoading}
        onTemplatesSaved={async () => {
          await loadInspectionTypes();
        }}
      />

      <InspectionAssignmentModal
        open={!!assignmentInspection}
        onClose={() => setAssignmentInspection(null)}
        projectId={projectId}
        inspection={assignmentInspection}
        teamOptions={teamOptions}
        teamLoading={teamLoading}
      />
      </>}
    </div>
  );
};

export default InspectionsView;
